// src/admin/pros/AddProModal.jsx
import React, { useState, useEffect } from "react";

export default function AddProModal({ open, onClose, onSave, categories }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    category: categories[0] || "",
    location: ""
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      setFormData({
        name: "",
        phone: "",
        category: categories[0] || "",
        location: ""
      });
      setErrors({});
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [open, onClose, categories]);

  if (!open) return null;

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Full name is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.category) newErrors.category = "Category is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const newPro = {
      id: `PRO-${String(Date.now()).slice(-4)}`,
      name: formData.name,
      phone: formData.phone,
      email: `${formData.name.toLowerCase().replace(/\s/g, ".")}@jirani.ke`,
      category: formData.category,
      location: formData.location,
      status: "pending",
      verification: "pending",
      earnings: 0,
      rating: 0,
      jobsCompleted: 0,
      lastActive: new Date().toISOString(),
      joined: new Date().toISOString().split("T")[0],
      featured: false,
      bio: "",
      lastPayout: 0,
      pendingPayout: 0,
      idVerified: false,
      docsSubmitted: false,
      approvalStatus: "pending",
      reviews: [],
      jobHistory: [],
    };
    onSave(newPro);
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.45)",
          zIndex: 399,
        }}
      />
      <div style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 440,
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        zIndex: 400,
        display: "flex",
        flexDirection: "column"
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <div style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 18,
            fontWeight: 700,
            color: "#0F172A"
          }}>
            Add New Pro
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "1px solid #E2E8F0",
              background: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "24px" }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: "#0F172A",
              marginBottom: 6
            }}>
              Full Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${errors.name ? "#DC2626" : "#E2E8F0"}`,
                borderRadius: 8,
                fontSize: 13,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                outline: "none"
              }}
            />
            {errors.name && (
              <div style={{ fontSize: 11, color: "#DC2626", marginTop: 4 }}>{errors.name}</div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: "#0F172A",
              marginBottom: 6
            }}>
              Phone *
            </label>
            <input
              type="tel"
              placeholder="+254 XXX XXX XXX"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${errors.phone ? "#DC2626" : "#E2E8F0"}`,
                borderRadius: 8,
                fontSize: 13,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                outline: "none"
              }}
            />
            {errors.phone && (
              <div style={{ fontSize: 11, color: "#DC2626", marginTop: 4 }}>{errors.phone}</div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: "#0F172A",
              marginBottom: 6
            }}>
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${errors.category ? "#DC2626" : "#E2E8F0"}`,
                borderRadius: 8,
                fontSize: 13,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                outline: "none",
                background: "#fff"
              }}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            {errors.category && (
              <div style={{ fontSize: 11, color: "#DC2626", marginTop: 4 }}>{errors.category}</div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: "#0F172A",
              marginBottom: 6
            }}>
              Location *
            </label>
            <input
              type="text"
              placeholder="e.g., Westlands, Nairobi"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${errors.location ? "#DC2626" : "#E2E8F0"}`,
                borderRadius: 8,
                fontSize: 13,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                outline: "none"
              }}
            />
            {errors.location && (
              <div style={{ fontSize: 11, color: "#DC2626", marginTop: 4 }}>{errors.location}</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px 24px",
          borderTop: "1px solid #E2E8F0",
          display: "flex",
          gap: 12,
          justifyContent: "flex-end"
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "8px 20px",
              background: "#fff",
              border: "1px solid #E2E8F0",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              color: "#64748B",
              cursor: "pointer",
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{
              padding: "8px 20px",
              background: "#0F172A",
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              color: "#fff",
              cursor: "pointer",
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
            Add Pro
          </button>
        </div>
      </div>
    </>
  );
}